import { authFetch } from 'shared/api/authFetch';
import { config } from 'shared/config/config';

export const requestJson = async (path, options = {}) => {
  const init = { ...options };
  if (init.body && typeof init.body !== 'string' && !(init.body instanceof FormData)) {
    init.body = JSON.stringify(init.body);
    init.headers = { 'Content-Type': 'application/json', ...(init.headers || {}) };
  }
  const response = await authFetch(`${config.API_URL}${path}`, init);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = payload?.detail;
    const message = typeof detail === 'string'
      ? detail
      : detail?.message || (Array.isArray(detail) ? detail.map((item) => item?.msg || String(item)).join('; ') : null);
    const error = new Error(message || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return payload;
};

const id = (value) => encodeURIComponent(String(value));

// The AI org chart.
export const listAgents = ({ directory = false } = {}) => requestJson(`/api/agents${directory ? '?directory=true' : ''}`);
export const getExecutionCatalog = () => requestJson('/api/agents/execution-catalog');
export const getAgent = (agentId) => requestJson(`/api/agents/${id(agentId)}`);
export const getAgentAccessOptions = (agentId) => requestJson(`/api/agents/${id(agentId)}/access-options`);
export const createAgent = (payload) => requestJson('/api/agents', { method: 'POST', body: payload });
export const updateAgent = (agentId, payload) => requestJson(`/api/agents/${id(agentId)}`, { method: 'PATCH', body: payload });

// Memory: the agent's shared notebook and the caller's private notes.
export const getAgentMemory = (agentId) => requestJson(`/api/agents/${id(agentId)}/memory`);
export const saveAgentMemory = (agentId, content, expectedRevision) =>
  requestJson(`/api/agents/${id(agentId)}/memory`, { method: 'PUT', body: { content, expected_revision: expectedRevision } });
export const saveAgentNotes = (agentId, content, expectedRevision) =>
  requestJson(`/api/agents/${id(agentId)}/notes`, { method: 'PUT', body: { content, expected_revision: expectedRevision } });

// The person's chats with an agent, newest first. Every chat is the same agent.
export const listAgentChats = (agentId, { limit = 50 } = {}) =>
  requestJson(`/api/conversations?agent_id=${id(agentId)}&limit=${Number(limit) || 50}`);
export const deleteAgentChat = (sessionId, agentId) =>
  requestJson(`/api/conversations/${id(sessionId)}${agentId ? `?agent_id=${id(agentId)}` : ''}`, { method: 'DELETE' });

// Needs you, updates, and the lightweight feed the shell polls.
export const getInbox = () => requestJson('/api/inbox');
export const answerAsk = (askId, { message = '', choice = null, files = [] } = {}) => {
  if (!files.length) {
    return requestJson(`/api/inbox/asks/${id(askId)}/answer`, { method: 'POST', body: { message, choice } });
  }
  // An agent asked for a spreadsheet or report: the files go to the work that asked.
  const body = new FormData();
  body.append('message', message);
  if (choice) body.append('choice', choice);
  files.forEach((file) => body.append('files', file));
  return requestJson(`/api/inbox/asks/${id(askId)}/answer-with-files`, { method: 'POST', body });
};
// A message to an Agent that was passed on to the person: an update is marked seen, a question
// closes without an answer (ADR-0020).
export const closeAgentMessage = (askId) =>
  requestJson(`/api/inbox/asks/${id(askId)}/close`, { method: 'POST', body: {} });
// Passing a question to whoever knows: a colleague, or someone invited to the workspace.
export const getPeopleToAsk = () => requestJson('/api/inbox/people');
export const forwardAsk = (askId, person) =>
  requestJson(`/api/inbox/asks/${id(askId)}/forward`, { method: 'POST', body: person });
export const getAgentFeed = () => requestJson('/api/me/agent-feed');
export const getUpdates = ({ limit = 20 } = {}) => requestJson(`/api/me/updates?limit=${Number(limit) || 20}`);
export const markUpdatesSeen = ({ ids = null, agentId = null } = {}) =>
  requestJson('/api/me/updates/seen', {
    method: 'POST',
    body: { ...(ids ? { ids: ids.map(String) } : {}), ...(agentId ? { agent_id: String(agentId) } : {}) },
  });
